import {
  ILibraryImportResponse,
  ILibraryRowsResponse,
  ILibrarySummaryResponse,
  LibraryClaimType,
} from "types/PayerProcedureServiceLine/PayerProcedureServiceLine";

import { api } from ".";

/**
 * CLE-2323 follow-on — RTK Query layer for the per-payer service-line library.
 *
 * Upload-style endpoints (`previewImport`, `import`) accept multipart bodies
 * — same pattern as the splitter / expander mutations. The other endpoints
 * are straightforward JSON GETs / DELETEs.
 */

interface ILibraryUploadRequest {
  file: File;
  payerId: number;
  claimType: LibraryClaimType;
}

interface ILibraryListRequest {
  payerId: number;
  claimType: LibraryClaimType;
  serviceLineName?: string;
  cptCode?: string;
  limit?: number;
  offset?: number;
}

const queries = api.injectEndpoints({
  endpoints: (builder) => ({
    previewLibraryImport: builder.mutation<
      ILibraryImportResponse,
      ILibraryUploadRequest
    >({
      query: ({ file, payerId, claimType }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("payerId", String(payerId));
        formData.append("claimType", claimType);
        return {
          url: "payer-procedure-service-lines/preview-import",
          method: "POST",
          body: formData,
        };
      },
    }),
    importLibrary: builder.mutation<
      ILibraryImportResponse,
      ILibraryUploadRequest
    >({
      query: ({ file, payerId, claimType }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("payerId", String(payerId));
        formData.append("claimType", claimType);
        return {
          url: "payer-procedure-service-lines/import",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["PayerProcedureServiceLines"],
    }),
    getLibrarySummary: builder.query<ILibrarySummaryResponse, void>({
      query: () => "payer-procedure-service-lines/summary",
      providesTags: ["PayerProcedureServiceLines"],
    }),
    getLibraryRows: builder.query<ILibraryRowsResponse, ILibraryListRequest>({
      query: (params) => {
        const qs = new URLSearchParams();
        qs.set("payerId", String(params.payerId));
        qs.set("claimType", params.claimType);
        if (params.serviceLineName)
          qs.set("serviceLineName", params.serviceLineName);
        if (params.cptCode) qs.set("cptCode", params.cptCode);
        if (params.limit !== undefined) qs.set("limit", String(params.limit));
        if (params.offset !== undefined)
          qs.set("offset", String(params.offset));
        return `payer-procedure-service-lines?${qs.toString()}`;
      },
      providesTags: ["PayerProcedureServiceLines"],
    }),
    deleteLibraryScope: builder.mutation<
      { data: { deletedRows: number }; message: string },
      { payerId: number; claimType: LibraryClaimType }
    >({
      query: ({ payerId, claimType }) => {
        const qs = new URLSearchParams();
        qs.set("payerId", String(payerId));
        qs.set("claimType", claimType);
        return {
          url: `payer-procedure-service-lines?${qs.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PayerProcedureServiceLines"],
    }),
  }),
});

export const {
  usePreviewLibraryImportMutation,
  useImportLibraryMutation,
  useGetLibrarySummaryQuery,
  useGetLibraryRowsQuery,
  useDeleteLibraryScopeMutation,
} = queries;
